import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { UseFormReturn } from 'react-hook-form'
import { useConfig } from 'wagmi'

import { calculateStusdsLiquidityBuffered } from '../stusds/calculateStusdsLiquidityBuffered'
import { stusdsWithdrawDataQueryFunction, stusdsWithdrawDataQueryKey } from '../stusds/stusdsWithdrawDataQuery'
import { savingsV2WithdrawDataQueryFunction, savingsV2WithdrawDataQueryKey } from '../v2/savingsV2WithdrawDataQuery'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { SavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { SavingsAccount, SavingsConverter } from '@/features/savings/logic/savings-converters/types'

export interface GetFormFieldsForWithdrawDialogParams {
  form: UseFormReturn<AssetInputSchema>
  tokenRepository: SavingsTokenRepository
  savingsAccount: SavingsAccount
  savingsTokenBalance: NormalizedNumber
  onMaxSet: (value: NormalizedNumber) => void
  timestamp: number
  chainId: number
}

export function useFormFieldsForWithdrawDialog({
  form,
  tokenRepository,
  savingsAccount,
  savingsTokenBalance,
  onMaxSet,
  timestamp,
  chainId,
}: GetFormFieldsForWithdrawDialogParams): FormFieldsForDialog {
  const { savingsToken, converter } = savingsAccount

  const wagmiConfig = useConfig()
  const isStusds = savingsToken.symbol === TokenSymbol('stUSDS')
  const { data: stusdsData } = useSuspenseQuery({
    queryKey: isStusds ? stusdsWithdrawDataQueryKey({ chainId }) : ['skip-stusds-withdraw-data-query'],
    queryFn: () => (isStusds ? stusdsWithdrawDataQueryFunction({ wagmiConfig, chainId }) : null),
  })
  const isSavingsV2 =
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDC') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDT') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spETH') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spPYUSD')
  const { data: savingsV2Data } = useSuspenseQuery({
    queryKey: isSavingsV2
      ? savingsV2WithdrawDataQueryKey({ chainId, savingsToken: savingsAccount.savingsToken })
      : ['skip-savings-v2-withdraw-data-query'],
    queryFn: () =>
      isSavingsV2
        ? savingsV2WithdrawDataQueryFunction({
            wagmiConfig,
            savingsToken: savingsAccount.savingsToken,
            underlyingToken: savingsAccount.underlyingToken,
            chainId,
          })
        : null,
  })

  // eslint-disable-next-line func-style
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const token = tokenRepository.findOneBySymbol(symbol)
  const outTokenAmount = converter.convertToAssets({ shares: savingsTokenBalance })

  let maxValue = outTokenAmount
  if (isStusds) {
    maxValue = getMaxStusdsValue({ savingsTokenBalance, stusdsData, converter, timestamp })
  }
  if (isSavingsV2) {
    maxValue = getMaxSavingsV2Value({ savingsTokenBalance, savingsV2Data, converter })
  }

  return {
    selectedAsset: token,
    selectedAssetBalance: outTokenAmount,
    value,
    changeAsset,
    maxValue,
    maxSelectedFieldName: 'isMaxSelected',
    onMaxSet,
  }
}

interface GetMaxStusdsValueParams {
  savingsTokenBalance: NormalizedNumber
  stusdsData: Awaited<ReturnType<typeof stusdsWithdrawDataQueryFunction>> | null
  converter: SavingsConverter
  timestamp: number
}
function getMaxStusdsValue({
  savingsTokenBalance,
  stusdsData,
  converter,
  timestamp,
}: GetMaxStusdsValueParams): NormalizedNumber {
  assert(stusdsData, 'stusdsData is required')

  const liquidityBuffered = calculateStusdsLiquidityBuffered({ converter, stusdsData, timestamp })

  const assetsBalance = converter.convertToAssets({ shares: savingsTokenBalance })

  return NormalizedNumber.min(liquidityBuffered, assetsBalance)
}

interface GetMaxSavingsV2ValueParams {
  savingsTokenBalance: NormalizedNumber
  savingsV2Data: Awaited<ReturnType<typeof savingsV2WithdrawDataQueryFunction>> | null
  converter: SavingsConverter
}
function getMaxSavingsV2Value({
  savingsTokenBalance,
  savingsV2Data,
  converter,
}: GetMaxSavingsV2ValueParams): NormalizedNumber {
  assert(savingsV2Data, 'savingsV2Data is required')
  const assetsBalance = converter.convertToAssets({ shares: savingsTokenBalance })

  return NormalizedNumber.min(savingsV2Data.liquidity, assetsBalance)
}
